/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import {
  autoUpdate,
  type ElementRects,
  offset,
  useFloating,
} from '@floating-ui/react';
import classNames from 'classnames';
import { useMemo } from 'react';

export type IndicatorVariant = 'border' | 'point';

interface IndicatorOptions {
  isOpen: boolean;
  indicatorVariant?: IndicatorVariant;
  indicatorPlacement?: IndicatorPlacement;
  targetRef: React.RefObject<HTMLElement | undefined | null>;
  hasPulse?: boolean;
  hasAnchorPortal?: boolean;
}

interface UseIndicatorReturn extends ReturnType<typeof useFloating> {
  style: React.CSSProperties;
  indicatorClasses?: string;
  hasAnchorPortal?: boolean;
}

export type IndicatorPlacement =
  | 'top-left'
  | 'top-middle'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-middle'
  | 'bottom-right'
  | 'middle-left'
  | 'middle-middle'
  | 'middle-right';

const crossAxisMult: Record<string, number> = {
  left: -1,
  middle: 0,
  right: 1,
};

const calcOffset = (
  variant: IndicatorVariant,
  placement: IndicatorPlacement,
  rects: ElementRects,
) => {
  if (variant === 'border') {
    // Border variant is always centered around the middle point
    return {
      crossAxis: 0,
      mainAxis: -rects.reference.height / 2 - rects.floating.height / 2,
    };
  }

  const [mainAxisPlacement, crossAxisPlacement] = placement.split('-');
  /** Note for future reference:
   * Main axis (vertical) is zero at the bottom border of the target.
   * Cross axis (horizontal) is zero at the middle of the target
   * */

  let mainAxis = 0;
  if (mainAxisPlacement === 'bottom') {
    // put the indicator one height of the point above the bottom border of the target
    mainAxis = -2 * rects.floating.height;
  } else if (mainAxisPlacement === 'middle') {
    // put the indicator in the middle of the target
    mainAxis = -rects.reference.height / 2 - rects.floating.height / 2;
  } else if (mainAxisPlacement === 'top') {
    // put the indicator one height of the point below the top border of the target
    mainAxis = -rects.reference.height + rects.floating.height;
  }

  return {
    crossAxis:
      crossAxisMult[crossAxisPlacement] *
      (rects.reference.width / 2 - rects.floating.width * 1.5),
    mainAxis,
  };
};

const getComputedBorderRadius = (
  element: HTMLElement | null | undefined,
): string | undefined => {
  if (!element) {
    return undefined;
  }

  // First check if there's an inline style
  if (element.style.borderRadius) {
    return element.style.borderRadius;
  }

  // If no inline style, get the computed style (includes CSS classes)
  const computedStyle = window.getComputedStyle(element);
  const borderRadius = computedStyle.borderRadius;

  // Return undefined if border-radius is '0px' or empty
  return borderRadius && borderRadius !== '0px' ? borderRadius : undefined;
};

export function useIndicator({
  isOpen,
  indicatorVariant = 'border',
  indicatorPlacement = 'top-right',
  targetRef,
  hasPulse = true,
}: IndicatorOptions): UseIndicatorReturn {
  const data = useFloating({
    elements: {
      reference: targetRef?.current,
    },
    middleware: [
      offset(({ rects }) =>
        calcOffset(indicatorVariant, indicatorPlacement, rects),
      ),
    ],
    open: isOpen,
    placement: 'bottom',
    whileElementsMounted: autoUpdate,
  });

  return useMemo(
    () => ({
      ...data,
      indicatorClasses: classNames('ndl-spotlight-target-indicator', {
        'ndl-spotlight-target-indicator-border': indicatorVariant === 'border',
        'ndl-spotlight-target-indicator-point': indicatorVariant === 'point',
        'ndl-spotlight-target-pulse': hasPulse,
      }),
      style: {
        borderRadius:
          indicatorVariant === 'border'
            ? getComputedBorderRadius(targetRef?.current)
            : '50%',
        height:
          indicatorVariant === 'border'
            ? `${targetRef?.current?.clientHeight ?? 0}px`
            : '8px',
        width:
          indicatorVariant === 'border'
            ? `${targetRef?.current?.clientWidth ?? 0}px`
            : '8px',
      },
    }),
    [data, indicatorVariant, targetRef, hasPulse],
  );
}
